/**
 * Isometric math + floor navigation for the Run Theater.
 *
 * The floor is a GRID_W×GRID_H tile grid. Grid → screen is the classic 2:1
 * dimetric projection; painter's depth is x+y (bigger = closer to the viewer,
 * drawn later). Characters navigate cell-to-cell with a BFS over walkable
 * cells + line-of-sight smoothing, so paths bend naturally around furniture
 * without ever clipping through it.
 */

import type { GridPoint } from './types'

export const GRID_W = 12
export const GRID_H = 9
export const HALF_W = 34
export const HALF_H = 17

/** Grid → screen (px). Positions are continuous — pass fractional tiles. */
export function project(p: GridPoint): { x: number; y: number } {
  return { x: (p.x - p.y) * HALF_W, y: (p.x + p.y) * HALF_H }
}

/** Painter's-algorithm depth — sort ascending before rendering. */
export function depthOf(p: GridPoint): number {
  return p.x + p.y
}

/** Screen-space distance between two grid points (for constant walk speed). */
export function screenDistance(a: GridPoint, b: GridPoint): number {
  const pa = project(a)
  const pb = project(b)
  return Math.hypot(pb.x - pa.x, pb.y - pa.y)
}

// ─── Navigation grid ─────────────────────────────────────────────────────────

const blocked = new Set<string>()
const cellKey = (x: number, y: number): string => `${x},${y}`

/** Mark a rectangle of cells unwalkable (furniture footprints). */
export function blockCells(cells: Array<[number, number]>): void {
  for (const [x, y] of cells) blocked.add(cellKey(x, y))
}

export function isWalkable(x: number, y: number): boolean {
  if (x < 0 || y < 0 || x >= GRID_W || y >= GRID_H) return false
  return !blocked.has(cellKey(x, y))
}

/** Centre of a cell in continuous grid coords. */
export function cellCenter(x: number, y: number): GridPoint {
  return { x: x + 0.5, y: y + 0.5 }
}

function toCell(p: GridPoint): [number, number] {
  return [Math.min(GRID_W - 1, Math.max(0, Math.floor(p.x))), Math.min(GRID_H - 1, Math.max(0, Math.floor(p.y)))]
}

/**
 * All cells a straight grid-space segment passes through (supercover line).
 * Used for path smoothing — a shortcut is allowed only if every touched cell
 * is walkable.
 */
function lineIsClear(a: GridPoint, b: GridPoint): boolean {
  const steps = Math.max(1, Math.ceil(Math.max(Math.abs(b.x - a.x), Math.abs(b.y - a.y)) * 4))
  for (let i = 0; i <= steps; i++) {
    const t = i / steps
    const x = a.x + (b.x - a.x) * t
    const y = a.y + (b.y - a.y) * t
    // Sample a small square around the point so we keep clearance from corners.
    for (const [dx, dy] of CLEARANCE) {
      const cx = Math.floor(x + dx)
      const cy = Math.floor(y + dy)
      if (cx < 0 || cy < 0 || cx >= GRID_W || cy >= GRID_H) continue
      if (blocked.has(cellKey(cx, cy))) return false
    }
  }
  return true
}

const CLEARANCE: Array<[number, number]> = [
  [0, 0],
  [0.22, 0],
  [-0.22, 0],
  [0, 0.22],
  [0, -0.22]
]

/**
 * BFS shortest path between two (continuous) grid points, returned as a
 * smoothed polyline of continuous waypoints INCLUDING the exact `to` point.
 * Falls back to a straight segment when either end is off-grid/unreachable
 * (spawn positions outside the room walk straight to the door).
 */
export function findPath(from: GridPoint, to: GridPoint): GridPoint[] {
  const [sx, sy] = toCell(from)
  const [tx, ty] = toCell(to)
  if ((sx === tx && sy === ty) || !isWalkable(sx, sy) || !isWalkable(tx, ty)) {
    return [to]
  }

  // Plain BFS — the grid is 12×9; nothing fancier pays for itself.
  const prev = new Map<string, string>()
  const queue: Array<[number, number]> = [[sx, sy]]
  const seen = new Set<string>([cellKey(sx, sy)])
  let found = false
  while (queue.length > 0 && !found) {
    const cur = queue.shift()
    if (!cur) break
    const [cx, cy] = cur
    for (const [dx, dy] of NEIGHBORS) {
      const nx = cx + dx
      const ny = cy + dy
      const key = cellKey(nx, ny)
      if (seen.has(key) || !isWalkable(nx, ny)) continue
      seen.add(key)
      prev.set(key, cellKey(cx, cy))
      if (nx === tx && ny === ty) {
        found = true
        break
      }
      queue.push([nx, ny])
    }
  }
  if (!found) return [to]

  // Reconstruct cell path → waypoints at cell centres.
  const cells: GridPoint[] = []
  let cursor = cellKey(tx, ty)
  const startKey = cellKey(sx, sy)
  while (cursor !== startKey) {
    const parts = cursor.split(',')
    cells.unshift(cellCenter(Number(parts[0]), Number(parts[1])))
    const p = prev.get(cursor)
    if (!p) break
    cursor = p
  }

  // Smooth: greedily skip waypoints reachable in a straight, clear line.
  const raw: GridPoint[] = [...cells.slice(0, -1), to]
  const smooth: GridPoint[] = []
  let anchor = from
  let i = 0
  while (i < raw.length) {
    let furthest = i
    for (let j = raw.length - 1; j > i; j--) {
      const candidate = raw[j]
      if (candidate && lineIsClear(anchor, candidate)) {
        furthest = j
        break
      }
    }
    const wp = raw[furthest]
    if (wp) {
      smooth.push(wp)
      anchor = wp
    }
    i = furthest + 1
  }
  const last = smooth[smooth.length - 1]
  if (!last || last.x !== to.x || last.y !== to.y) smooth.push(to)
  return smooth
}

const NEIGHBORS: Array<[number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1]
]

// ─── Easing ──────────────────────────────────────────────────────────────────

/** Gentle ease-in-out for whole-path walk progress. */
export function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
}

/** Total polyline length in screen px (walk duration = length / speed). */
export function pathLength(from: GridPoint, waypoints: GridPoint[]): number {
  let len = 0
  let prev = from
  for (const wp of waypoints) {
    len += screenDistance(prev, wp)
    prev = wp
  }
  return len
}

/** Point + heading along a polyline at eased progress t ∈ [0,1]. */
export function pointAlong(
  from: GridPoint,
  waypoints: GridPoint[],
  t: number
): { pos: GridPoint; facing: 1 | -1; yaw: number } {
  const total = pathLength(from, waypoints)
  if (total === 0) {
    const end = waypoints[waypoints.length - 1] ?? from
    return { pos: end, facing: 1, yaw: 0 }
  }
  let remaining = easeInOut(Math.min(1, Math.max(0, t))) * total
  let prev = from
  let facing: 1 | -1 = 1
  let yaw = 0
  for (const wp of waypoints) {
    const seg = screenDistance(prev, wp)
    const pPrev = project(prev)
    const pWp = project(wp)
    const dxs = pWp.x - pPrev.x
    if (Math.abs(dxs) > 0.5) facing = dxs > 0 ? 1 : -1
    // World heading: grid x → world x, grid y → world z; yaw 0 faces +z.
    const dx = wp.x - prev.x
    const dz = wp.y - prev.y
    if (Math.abs(dx) > 1e-4 || Math.abs(dz) > 1e-4) yaw = Math.atan2(dx, dz)
    if (seg >= remaining && seg > 0) {
      const k = remaining / seg
      return {
        pos: { x: prev.x + (wp.x - prev.x) * k, y: prev.y + (wp.y - prev.y) * k },
        facing,
        yaw
      }
    }
    remaining -= seg
    prev = wp
  }
  return { pos: prev, facing, yaw }
}
